import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/shared/contexts/ToastContext'
import { exportNSIPSData } from '@/features/internal/apis/nsipsExport'
import {
  exportMetricsRecords,
  MetricsExportResult,
} from '@/features/internal/metrics/apis/metrics'
import { NSIPSFormData } from '@/features/internal/types/nsips'
import { MetricsExportRequest } from '@/features/internal/metrics/types/metrics'
import { NSIPS_ERRORS } from '@/constants/nsips'
import { getErrorMessage, isAuthError } from '@/shared/utils/errorHandling'
import { downloadBlob, generateDateFilename } from '@/shared/utils/downloadBlob'

// ========================================
// useExportNSIPSMutation
// ========================================

/**
 * NSIPS エクスポート Mutation
 *
 * 責務:
 * - NSIPS データのエクスポート API 呼び出し
 * - ファイルダウンロード処理
 * - エラーハンドリング（認証エラー、ダウンロード失敗）
 *
 * 使用例:
 * ```tsx
 * const exportMutation = useExportNSIPSMutation()
 * await exportMutation.mutateAsync(formData)
 * ```
 */
export const useExportNSIPSMutation = () => {
  const { showToast } = useToast()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (formData: NSIPSFormData) => {
      const { blob, filename } = await exportNSIPSData(formData)
      downloadBlob(blob, filename)
    },
    onSuccess: () => {
      showToast('NSIPSデータのエクスポートが完了しました', 'success')
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error)

      if (isAuthError(error)) {
        showToast(NSIPS_ERRORS.AUTH_ERROR, 'error')
        navigate('/login')
      } else if (errorMessage.includes('ダウンロード')) {
        showToast(NSIPS_ERRORS.DOWNLOAD_FAILED, 'error')
      } else {
        showToast(NSIPS_ERRORS.EXPORT_RETRY, 'error')
      }
    },
  })
}

// ========================================
// useExportMetricsRecordsMutation
// ========================================

/**
 * メトリクス CSV エクスポート Mutation
 *
 * 責務:
 * - メトリクスレコードの CSV エクスポート API 呼び出し
 * - ファイルダウンロード処理
 * - エラーハンドリング
 *
 * 使用例:
 * ```tsx
 * const exportMutation = useExportMetricsRecordsMutation()
 * await exportMutation.mutateAsync(params)
 * ```
 */
export const useExportMetricsRecordsMutation = () => {
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      params: MetricsExportRequest
    ): Promise<MetricsExportResult> => {
      const result = await exportMetricsRecords(params)
      const filename = generateDateFilename('metrics', 'csv')
      downloadBlob(result.blob, filename)
      return result
    },
    onSuccess: (result: MetricsExportResult) => {
      if (result.isTruncated) {
        showToast(
          `対象データが${result.totalCount.toLocaleString()}件ありますが、上限の${result.exportLimit.toLocaleString()}件までを出力しました。期間を絞って再度お試しください。`,
          'warning'
        )
      } else {
        showToast('CSVエクスポートが完了しました', 'success')
      }
    },
    onError: () => {
      showToast('CSVエクスポートに失敗しました', 'error')
    },
  })
}
